/**
 * Phone numbers as data: the country list, and the two conversions the UI
 * needs. Apart from the component so a fast refresh of the field does not
 * invalidate the table, and so the same helpers can be read from a form that
 * does not render the field.
 */

/** The countries the product speaks to, plus the ones its members write from. */
export const PHONE_COUNTRIES: { iso: string; dial: string; flag: string; name: string }[] = [
  { iso: 'BR', dial: '55', flag: '🇧🇷', name: 'Brasil' },
  { iso: 'PT', dial: '351', flag: '🇵🇹', name: 'Portugal' },
  { iso: 'US', dial: '1', flag: '🇺🇸', name: 'United States' },
  { iso: 'CA', dial: '1', flag: '🇨🇦', name: 'Canada' },
  { iso: 'MX', dial: '52', flag: '🇲🇽', name: 'México' },
  { iso: 'AR', dial: '54', flag: '🇦🇷', name: 'Argentina' },
  { iso: 'CL', dial: '56', flag: '🇨🇱', name: 'Chile' },
  { iso: 'CO', dial: '57', flag: '🇨🇴', name: 'Colombia' },
  { iso: 'PE', dial: '51', flag: '🇵🇪', name: 'Perú' },
  { iso: 'UY', dial: '598', flag: '🇺🇾', name: 'Uruguay' },
  { iso: 'PY', dial: '595', flag: '🇵🇾', name: 'Paraguay' },
  { iso: 'BO', dial: '591', flag: '🇧🇴', name: 'Bolivia' },
  { iso: 'EC', dial: '593', flag: '🇪🇨', name: 'Ecuador' },
  { iso: 'VE', dial: '58', flag: '🇻🇪', name: 'Venezuela' },
  { iso: 'ES', dial: '34', flag: '🇪🇸', name: 'España' },
  { iso: 'IT', dial: '39', flag: '🇮🇹', name: 'Italia' },
  { iso: 'DE', dial: '49', flag: '🇩🇪', name: 'Deutschland' },
  { iso: 'AT', dial: '43', flag: '🇦🇹', name: 'Österreich' },
  { iso: 'CH', dial: '41', flag: '🇨🇭', name: 'Schweiz' },
  { iso: 'FR', dial: '33', flag: '🇫🇷', name: 'France' },
  { iso: 'GB', dial: '44', flag: '🇬🇧', name: 'United Kingdom' },
  { iso: 'IE', dial: '353', flag: '🇮🇪', name: 'Ireland' },
  { iso: 'NL', dial: '31', flag: '🇳🇱', name: 'Nederland' },
  { iso: 'BE', dial: '32', flag: '🇧🇪', name: 'België' },
  { iso: 'PL', dial: '48', flag: '🇵🇱', name: 'Polska' },
  { iso: 'SE', dial: '46', flag: '🇸🇪', name: 'Sverige' },
  { iso: 'NO', dial: '47', flag: '🇳🇴', name: 'Norge' },
  { iso: 'DK', dial: '45', flag: '🇩🇰', name: 'Danmark' },
  { iso: 'FI', dial: '358', flag: '🇫🇮', name: 'Suomi' },
  { iso: 'AO', dial: '244', flag: '🇦🇴', name: 'Angola' },
  { iso: 'MZ', dial: '258', flag: '🇲🇿', name: 'Moçambique' },
  { iso: 'CV', dial: '238', flag: '🇨🇻', name: 'Cabo Verde' },
  { iso: 'JP', dial: '81', flag: '🇯🇵', name: '日本' },
  { iso: 'AU', dial: '61', flag: '🇦🇺', name: 'Australia' },
  { iso: 'NZ', dial: '64', flag: '🇳🇿', name: 'New Zealand' },
  { iso: 'ZA', dial: '27', flag: '🇿🇦', name: 'South Africa' }
]

const byIso = (iso?: string | null) =>
  PHONE_COUNTRIES.find((country) => country.iso === String(iso || '').toUpperCase()) || null

/** `pt-BR` → `BR`, when that is a country this list knows. */
export const countryFromLocale = (locale?: string | null): string | null => {
  const region = String(locale || '').split('-')[1]
  const iso = region ? region.toUpperCase() : null
  return iso && byIso(iso) ? iso : null
}

/**
 * `+5541999998888` → `{ country: 'BR', national: '41999998888' }`.
 *
 * Longest dial code first, so `+351` is not read as `+35`. An ambiguous code
 * lands on the first country listed, which is why the chosen one is stored
 * alongside the number and passed in when it is known.
 */
export const splitE164 = (
  e164?: string | null,
  known?: string | null
): { country: string | null; national: string } => {
  if (!e164) return { country: known ?? null, national: '' }

  const digits = String(e164).replace(/\D/g, '')
  const hinted = byIso(known)

  if (hinted && digits.startsWith(hinted.dial)) {
    return { country: hinted.iso, national: digits.slice(hinted.dial.length) }
  }

  const match = [...PHONE_COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((country) => digits.startsWith(country.dial))

  return match
    ? { country: match.iso, national: digits.slice(match.dial.length) }
    : { country: known ?? null, national: digits }
}


/**
 * `+5541999998888` → `+55 41 99999-8888`.
 *
 * Only for reading. What travels and what is stored stays E.164 — a formatted
 * string in the column is how the same person becomes two records.
 */
export const formatE164 = (e164?: string | null, country?: string | null): string => {
  if (!e164) return ''

  const { country: iso, national } = splitE164(e164, country)
  const dial = PHONE_COUNTRIES.find((option) => option.iso === iso)?.dial
  if (!dial || !national) return String(e164)

  /* Brazilian mobiles are the common case here and read badly unbroken:
     `41999998888` against `41 99999-8888`. Everything else gets spaced in
     threes, which is wrong in some countries and unreadable in none. */
  const grouped = national.length === 10 || national.length === 11
    ? `${national.slice(0, 2)} ${national.slice(2, national.length - 4)}-${national.slice(-4)}`
    : national.replace(/(\d{3})(?=\d)/g, '$1 ')

  return `+${dial} ${grouped}`
}

/**
 * The national part as people write it at home, for the input box:
 * `BR` + `11987654321` → `(11) 98765-4321`; `US` + `4155552671` →
 * `(415) 555-2671`. Anything the mask does not know is spaced in threes.
 * Display only — the field still emits E.164.
 */
export const formatNational = (iso?: string | null, national?: string | null): string => {
  const digits = String(national || '').replace(/\D/g, '')
  if (!digits) return ''
  const country = String(iso || '').toUpperCase()

  if (country === 'BR' && (digits.length === 10 || digits.length === 11)) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, -4)}-${digits.slice(-4)}`
  }
  if ((country === 'US' || country === 'CA') && digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return digits.replace(/(\d{3})(?=\d)/g, '$1 ')
}
